/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, NotifyService, UserService, UtilsService } from '../../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { OrganisationsApiService } from '../../../../shared/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-import-users',
    templateUrl: './portal-import-users.component.html',
    styleUrls: ['./portal-import-users.component.scss']
})
export class PortalImportUsersComponent extends BaseComponent {
    /**
     * Table rows.
     */
    public tableRows: any[] = [];

    /**
     * Store submission errors
     */
    public rowStatus: any[] = [];

    /**
     * Store submission errors
     */
    public errors: any[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private organisationsApiService: OrganisationsApiService,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        const navigationState: any = this.router.getCurrentNavigation()?.extras?.state;
        if (!navigationState) {
            this.backToParentRoute();
        }

        this.populateTableRows(navigationState?.csvData);
    }

    /**
     * Navigate back to user.
     */
    public backToParentRoute(): void {
        this.router.navigate(['../../'], { relativeTo: this.activatedRoute }).then().catch();
    }

    /**
     * Submit users
     */
    public submitUsers(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.errors = [];
            const clonedTableRows: any[] = this.utilsService.duplicateObject(this.tableRows);
            const organisationId = this.utilsService.duplicateObject(this.userService.getUser('organisationId'));

            const processNextUser = (index: number): void => {
                if (index >= clonedTableRows.length) {
                    this.updatePage();
                    if (this.errors.length === 0) {
                        observer.next();
                    } else {
                        observer.error(new Error('Some users failed to import'));
                    }
                    observer.complete();
                }

                const row: any = clonedTableRows[index];
                this.rowStatus[index] = { type: 'active', message: 'processing' };

                this.inviteUser(this.formatUserVales(row), organisationId)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (): void => {
                            this.rowStatus[index] = { type: 'success', message: 'success' };
                            processNextUser(index + 1);
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('PortalImportUsersComponent: Submit', error.message);
                            this.rowStatus[index] = { type: 'error', message: error.message };
                            this.errors.push(row);
                            processNextUser(index + 1);
                        }
                    });
            };

            processNextUser(0);
        });
    }

    /**
     * Format the form values.
     * @param userValues
     * @private
     */
    private formatUserVales(userValues: any): any {
        return {
            email: userValues.email,
            disabled: userValues.disabled,
            permissions: 2,
            roles: this.formatArray(userValues.roles),
            groups: this.formatArray(userValues.groups)
        };
    }

    /**
     * Format Array.
     * @param data
     * @private
     */
    private formatArray(data: any): string[] {
        const array: any = Array.isArray(data) ? data : [data];

        return data ? array.map(String) : [];
    }

    /**
     * Invite new user to organisation.
     * @param userValues
     * @param organisationId
     * @private
     */
    private inviteUser(userValues: any, organisationId: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.organisationsApiService.inviteMember(organisationId, userValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data.id);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update page.
     * @private
     */
    private updatePage(): void {
        this.tableRows = [];
        this.notifyService.unmuteLocalNotifications();
        this.loadingService.loadingButtonEmitter.next(false);
        if (!this.errors.length) {
            this.backToParentRoute();
        } else {
            this.tableRows = [...this.errors];
        }
    }

    /**
     * Populate table rows.
     * @param csvData
     * @private
     */
    private populateTableRows(csvData: any[]): void {
        this.tableRows = [];
        let index: number = 0;
        for (const row of csvData) {
            this.tableRows.push({
                id: index,
                firstName: row.firstName,
                lastName: row.lastName,
                email: row.email,
                roles: row.roles,
                groups: row.groups,
                disabled: row.disabled
            });

            this.rowStatus.push({
                id: index,
                message: 'waiting',
                type: 'waiting'
            });

            index++;
        }
    }
}
